import {
  useRef,
  useEffect,
  useImperativeHandle,
  forwardRef,
  type ForwardedRef,
  type CSSProperties,
} from 'react';
import PaperViewPDF, {
  type PaperViewPdfContainer,
  type PDFViewerConfig,
  type PluginRegistry,
} from '@paperviewpdf/viewer';

// Re-export everything from viewer for convenience
export * from '@paperviewpdf/viewer';

export interface PDFViewerProps {
  /** Full configuration for the PDF viewer */
  config?: PDFViewerConfig;
  /** CSS class name for the container div */
  className?: string;
  /** Inline styles for the container div */
  style?: CSSProperties;
  /** Callback when the viewer is initialized */
  onInit?: (container: PaperViewPdfContainer) => void;
  /** Callback when the registry is ready */
  onReady?: (registry: PluginRegistry) => void;
}

export interface PDFViewerRef {
  /** The PaperViewPdfContainer element */
  container: PaperViewPdfContainer | null;
  /** Promise that resolves to the PluginRegistry */
  registry: Promise<PluginRegistry> | null;
}

/**
 * React component for embedding PDF documents with full UI
 *
 * @example
 * ```tsx
 * import { PDFViewer } from '@paperviewpdf/react-viewer';
 *
 * function App() {
 *   return (
 *     <PDFViewer
 *       config={{
 *         src: '/document.pdf',
 *         theme: { preference: 'system' },
 *       }}
 *       style={{ width: '100%', height: '100vh' }}
 *       onReady={(registry) => {
 *         console.log('PDF viewer ready', registry);
 *       }}
 *     />
 *   );
 * }
 * ```
 *
 * @example
 * ```tsx
 * // With XFDF annotation import/export
 * import { PDFViewer, useXfdf } from '@paperviewpdf/react-viewer';
 *
 * function App() {
 *   const viewerRef = useRef<PDFViewerRef>(null);
 *
 *   const handleExport = async () => {
 *     const registry = await viewerRef.current?.registry;
 *     const xfdf = registry?.getPlugin('xfdf');
 *     const xfdfString = xfdf?.provides.exportXfdf({ prettyPrint: true });
 *     console.log(xfdfString);
 *   };
 *
 *   return (
 *     <>
 *       <PDFViewer ref={viewerRef} config={{ src: '/doc.pdf' }} />
 *       <button onClick={handleExport}>Export XFDF</button>
 *     </>
 *   );
 * }
 * ```
 */
export const PDFViewer = forwardRef(function PDFViewer(
  props: PDFViewerProps,
  ref: ForwardedRef<PDFViewerRef>,
) {
  const { config = {}, className, style, onInit, onReady } = props;
  const containerRef = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<PaperViewPdfContainer | null>(null);

  useImperativeHandle(ref, () => ({
    get container() {
      return viewerRef.current;
    },
    get registry() {
      return viewerRef.current?.registry ?? null;
    },
  }));

  useEffect(() => {
    if (!containerRef.current) return;

    // Track if this effect instance is still active (for Strict Mode)
    let isActive = true;

    // IMPORTANT: Capture the container reference in a closure
    // This ensures we can clean up even after React unmounts the component
    const containerElement = containerRef.current;

    // Initialize the viewer with the config prop
    const viewer = PaperViewPDF.init({
      type: 'container',
      target: containerElement,
      ...config,
    });

    if (viewer) {
      viewerRef.current = viewer;
      onInit?.(viewer);

      // Call onReady when registry is available, but only if still active
      if (onReady) {
        viewer.registry.then((registry) => {
          // Only call onReady if this effect instance is still active
          // This prevents stale callbacks in React Strict Mode
          if (isActive) {
            onReady(registry);
          }
        });
      }
    }

    return () => {
      // Mark this effect instance as inactive
      isActive = false;

      // Cleanup: remove the viewer element using captured reference
      // We use the captured containerElement instead of containerRef.current
      // because React may have already unmounted and cleared the ref
      containerElement.innerHTML = '';
      viewerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <div ref={containerRef} className={className} style={style} />;
});

export default PDFViewer;
